import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import styles from "./SelectPhotoModal.module.css";
import classNames from "classnames";
import { useSelectPhotoModal } from "./useSelectPhotoModal";

import Button from "../../Shared/Button";
import Loader from "shared/Loader";

/*
 * Interactive component. A modal that allows the user to select and add an image.
 * The file selector does not limit file types, but the file will not be shown if it is not a valid image.
 * If there is already a current photo, it does not appear in the file select when the modal is first opened.
 * If minWidth/minHeight are set and the image is smaller, a warning will appear.
 */

export function SelectPhotoModal({
  currentPhotoSrc,
  setCurrentPhotoSrc,
  isVisible,
  setIsVisible,
  onSubmit,
  index,
  disableOnErrorMsg,
  minWidth,
  minHeight,
}) {
  const imagePreviewRef = useRef(null);
  const [errorMsg, setErrorMsg] = useState(false);

  const { uploadImage, isLoading } = useSelectPhotoModal();

  const dragOver = (e) => {
    e.preventDefault();
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (err) => reject(err);
    });

  const onDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      try {
        const base64 = await fileToBase64(file);

        uploadImage(
          {
            body: {
              name: file.name,
              image: base64,
            },
          },
          {
            onSuccess: (updatedItem) => {
              setCurrentPhotoSrc(updatedItem.url);
            },
            onError: (err) => {
              console.error("Upload failed:", err);
            },
          }
        );
      } catch (err) {
        console.error("Error reading file:", err);
      }
    }
  };

  const onFileInputChange = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      try {
        const base64 = await fileToBase64(file);

        uploadImage(
          {
            body: {
              name: file.name,
              image: base64,
            },
          },
          {
            onSuccess: (updatedItem) => {
              setCurrentPhotoSrc(updatedItem.url);
            },
            onError: (err) => {
              console.error("Upload failed:", err);
            },
          }
        );
      } catch (err) {
        console.error("Error reading file:", err);
      }
    }
  };

  const onSubmitPhoto = () => {
    // alert("Use this Photo Clicked");
    onSubmit(currentPhotoSrc, index);
  };

  return (
    <div
      className={classNames(styles.overlay, {
        [styles.hidden]: !isVisible,
      })}
    >
      <div className={styles.container}>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <div className={styles.warning}>
              {errorMsg && (
                <p className={styles.warningText}>
                  Warning! Images must be at least {minWidth}px X {minHeight}px.
                </p>
              )}
            </div>

            <div
              className={styles.outerContainer}
              onDragOver={dragOver}
              onDrop={onDrop}
            >
              <div>
                {currentPhotoSrc ? (
                  <img
                    src={currentPhotoSrc}
                    className={styles.preview}
                    alt=""
                    onLoad={(e) => {
                      const img = e.target;
                      if (
                        minWidth != null &&
                        minHeight != null &&
                        (img.naturalWidth < minWidth ||
                          img.naturalHeight < minHeight)
                      ) {
                        setErrorMsg(true);
                      } else {
                        setErrorMsg(false);
                      }
                    }}
                  />
                ) : (
                  <p className={styles.placeholder}>
                    Select a file or drag one here
                  </p>
                )}
              </div>
            </div>
            <div className={styles.btnDiv}>
              {currentPhotoSrc && (
                <Button
                  onClick={onSubmitPhoto}
                  text="Use This Photo"
                  className={styles.useBtn}
                  disabled={errorMsg && disableOnErrorMsg}
                />
              )}

              <input
                type="file"
                id="imagePreview"
                ref={imagePreviewRef}
                className={styles.file}
                onChange={onFileInputChange}
              />

              <Button
                onClick={() => imagePreviewRef.current.click()}
                text="Select Photo"
                className={styles.selectBtn}
              />

              <Button
                onClick={setIsVisible}
                text="Cancel"
                className={styles.cancelBtn}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

SelectPhotoModal.propTypes = {
  /**
   * Src of the selected image.
   */
  currentPhotoSrc: PropTypes.string,
  /**
   * Function that fires when a new photo is selected/unselected.
   */
  setCurrentPhotoSrc: PropTypes.func,
  /**
   * Boolean that determines if the modal is visible. Defaults to false.
   */
  isVisible: PropTypes.bool,
  /**
   * Function that fires when the submit button is clicked.
   */
  onSubmit: PropTypes.func,
  /**
   * Function that fires when modal is not visible.
   */
  setIsVisible: PropTypes.func,
  /**
   * Minimum width in px required before showing a warning. Pass null to skip size validation.
   */
  minWidth: PropTypes.number,
  /**
   * Minimum height in px required before showing a warning. Pass null to skip size validation.
   */
  minHeight: PropTypes.number,
};

SelectPhotoModal.defaultProps = {
  minWidth: 600,
  minHeight: 600,
};
